import TextButton from 'components/Luxkit/Button/TextButton'
import Heading from 'components/Luxkit/Typography/Heading'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ExperienceListTileCarousel from '../ExperienceLists/ExperienceListTileCarousel'
import useExperienceList from 'hooks/Experiences/useExperienceList'
import useExperienceSearchUrl from 'hooks/Experiences/useExperienceSearchUrl'
import BodyText from 'components/Luxkit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineMapIcon from 'components/Luxkit/Icons/line/LineMapIcon'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'

const CarouselList = styled.div`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(20)};
  display: grid;
  row-gap: ${rem(20)};
  grid-template-areas:
      "title"
      "carousel"
      "actions";

  ${mediaQueryUp.tablet} {
    grid-template-areas:
      "title actions"
      "carousel carousel";
    padding-bottom: ${rem(40)};
  }
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: title;
`

const StyledExperienceListTileCarousel = styled(ExperienceListTileCarousel)`
  grid-area: carousel;
`

const Actions = styled.div`
  align-self: end;
  margin-left: auto;
  grid-area: actions;
  display: flex;
  gap: ${rem(12)};
`

interface Props {
  latitude: number;
  longitude: number;
  place: App.Place;
  title: string;
  checkIn: string;
  checkOut: string;
  distance?: string;
  subtitle?: string;
}

function ExperiencesPageCarousel(props: Props) {
  const {
    title,
    place,
    latitude,
    longitude,
    checkIn,
    checkOut,
    distance = '50km',
    subtitle,
  } = props

  const [list, experiences] = useExperienceList({
    latitude,
    longitude,
    from: checkIn,
    to: checkOut,
    distance,
  })

  const [searchUrl] = useExperienceSearchUrl({ place })
  const [searchMapUrl] = useExperienceSearchUrl({ place, isMapUrl: true })

  const trackEvent = useTrackExperienceOfferEvent()

  const handleViewMapClick = useCallback(() => {
    trackEvent('VIEW_ON_MAP_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  const handleViewAllClick = useCallback(() => {
    trackEvent('VIEW_ALL_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  // we want to show a minimum of one full row on the experiences page, which is 4
  if (list.ids.length < 4) {
    return null
  }

  return (
    <CarouselList>
      <StyledVerticalSpacer gap={8}>
        <Heading variant="heading3">{title}</Heading>
        {subtitle && <BodyText variant="medium" colour="neutral-two">{subtitle}</BodyText>}
      </StyledVerticalSpacer>
      <StyledExperienceListTileCarousel
        experiences={experiences}
        listFetching={list.fetching}
      />
      <Actions>
        <TextButton
          kind="tertiary"
          horizontalOutdent="end"
          to={searchMapUrl}
          startIcon={<LineMapIcon />}
          onClick={handleViewMapClick}
        >
          View on map
        </TextButton>
        <TextButton
          kind="tertiary"
          variant="dark"
          to={searchUrl}
          onClick={handleViewAllClick}
        >
          View all
        </TextButton>
      </Actions>
    </CarouselList>

  )
}

export default ExperiencesPageCarousel
